import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useI18n } from '../content/i18n';
import './LearningFeaturesSection.css';

gsap.registerPlugin(ScrollTrigger);

const LearningFeaturesSection = () => {
  const { t } = useI18n();
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const panelRef = useRef(null);
  const descriptionRef = useRef(null);
  const [showDescriptions, setShowDescriptions] = useState(false);
  const arrowRefs = useRef([]);

  // SVG 아이콘 컴포넌트
  const SkipPreviousIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '100%', height: '100%' }}>
      <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
    </svg>
  );

  const PauseIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '100%', height: '100%' }}>
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
    </svg>
  );

  const SkipNextIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '100%', height: '100%' }}>
      <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
    </svg>
  );

  const RepeatIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '100%', height: '100%' }}>
      <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
    </svg>
  );

  // 버튼 데이터
  const buttons = [
    {
      id: 0, 
      type: 'mimic',
      icon: '/resource/mimic_icon.png',
      label: t.learningFeatures.panel.items[0].title,
      isImage: true
    },
    { 
      id: 1, 
      type: 'prev',
      IconComponent: SkipPreviousIcon,
      label: t.learningFeatures.panel.items[1].title
    },
    {
      id: 2, 
      type: 'play',
      IconComponent: PauseIcon,
      label: t.learningFeatures.panel.items[2].title,
      isLarge: true
    },
    { 
      id: 3, 
      type: 'next',
      IconComponent: SkipNextIcon,
      label: t.learningFeatures.panel.items[3].title
    },
    { 
      id: 4, 
      type: 'repeat',
      IconComponent: RepeatIcon,
      label: t.learningFeatures.panel.items[4].title
    }
  ];

  // 스크롤 애니메이션
  useEffect(() => {
    const ctx = gsap.context(() => {
      // 타이틀 애니메이션
      gsap.fromTo(
        titleRef.current,
        { 
          opacity: 0,
          y: 50,
          scale: 0.9
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            end: 'top 50%',
            scrub: 1,
          },
        }
      );

      // 패널 애니메이션
      gsap.fromTo(
        panelRef.current,
        { 
          opacity: 0,
          scale: 0.8
        },
        {
          opacity: 1,
          scale: 1,
          scrollTrigger: {
            trigger: panelRef.current,
            start: 'top 80%',
            end: 'top 50%',
            scrub: 1,
            onEnter: () => {
              // 패널이 화면에 나타나면 설명 표시 시작
              setTimeout(() => setShowDescriptions(true), 500);
            },
          },
        }
      );

      // 설명 텍스트 애니메이션 (PC: 오른쪽에서, 모바일: 아래에서)
      if (descriptionRef.current) {
        const isDesktop = window.innerWidth > 968;
        gsap.fromTo(
          descriptionRef.current,
          { 
            opacity: 0,
            x: isDesktop ? 50 : 0,
            y: isDesktop ? 0 : 30
          },
          {
            opacity: 1,
            x: 0,
            y: 0,
            scrollTrigger: {
              trigger: descriptionRef.current,
              start: 'top 80%',
              end: 'top 50%',
              scrub: 1,
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // 순차 애니메이션 (페어로)
  useEffect(() => {
    if (!showDescriptions) return;

    // 각 선-roundbox 페어의 총 시간
    const pairDuration = 0.8 + 0.5; // 선(0.8초) + chip(0.5초) = 1.3초

    // 모든 버튼의 화살표와 Chip을 페어로 순차적으로 애니메이션
    buttons.forEach((button, index) => {
      const arrow = arrowRefs.current[button.id];
      if (!arrow) return;

      const line = arrow.querySelector('.arrow-line');
      const chip = arrow.querySelector('.arrow-chip');

      if (line && chip) {
        // 각 페어의 시작 시간 (이전 페어가 완료된 후)
        const pairStartTime = index * pairDuration;

        // 화살표 선 그리기 애니메이션
        gsap.fromTo(line,
          { strokeDashoffset: 200 },
          { 
            strokeDashoffset: 0, 
            duration: 0.8,
            delay: pairStartTime,
            ease: 'power2.out'
          }
        );

        // Chip 텍스트 페이드인 (해당 선이 완전히 그려진 후)
        gsap.fromTo(chip,
          { opacity: 0, y: -10 },
          { 
            opacity: 1, 
            y: 0,
            duration: 0.5,
            delay: pairStartTime + 0.8,
            ease: 'power2.out'
          }
        );
      }
    });
  }, [showDescriptions, buttons]);

  return (
    <section className="learning-features-section" ref={sectionRef}>
      <div className="learning-features-content container container--wide">
        <h2 className="learning-features-title" ref={titleRef}>
          {t.learningFeatures.title}
        </h2>

        <div className="panel-layout">
          <div className="panel-container" ref={panelRef}>
          <div className="playback-panel">
            {/* 슬라이더 */}
            <div className="slider-container">
              <input 
                type="range" 
                min="0" 
                max="100" 
                defaultValue="30"
                className="progress-slider"
                style={{'--progress': '30%'}}
              />
            </div>

            {/* 시간 표시 */}
            <div className="time-display">
              <span className="time-text">00:45</span>
              <span className="time-text">02:30</span>
            </div>

            {/* 버튼 행 */}
            <div className="buttons-row">
              {buttons.map((button) => (
              <div
                  key={button.id} 
                  className={`button-wrapper ${button.isLarge ? 'large' : ''}`}
                >
                  {/* 화살표와 Chip - 아래로 표시 */}
                  {showDescriptions && (
                    <div 
                      className="arrow-container"
                      ref={el => arrowRefs.current[button.id] = el}
              >
                      <svg className="arrow-svg" viewBox="0 0 2 150">
                        <line
                          className="arrow-line"
                          x1="1"
                          y1="0"
                          x2="1"
                          y2="150"
                          stroke="white"
                          strokeWidth="2"
                          strokeDasharray="200"
                          strokeDashoffset="200"
                        />
                        <polygon
                          points="1,150 -4,142 6,142"
                          fill="white"
                        />
                      </svg>
                      <div className="arrow-chip">
                        {button.label}
                      </div>
                    </div>
                  )}

                  {/* 버튼 */}
                  <div
                    className={`panel-button ${button.type} ${button.isLarge ? 'large' : ''}`}
                    aria-label={button.label}
                  >
                    {button.isImage ? (
                      <img src={button.icon} alt="MIMIC" className="button-icon-img" />
                    ) : button.IconComponent ? (
                      <div className="button-icon">
                        <button.IconComponent />
                      </div>
                    ) : null}
                  </div>
              </div>
            ))}
          </div>
          </div>
        </div>

        <div className="panel-description" ref={descriptionRef}>
          <p className="description-text">
            익숙하지만 <span className="highlight-text">강력한</span> 반복 학습 제어를 지원합니다.
          </p>
        </div>
      </div>
      </div>
    </section>
  );
};

export default LearningFeaturesSection;

